import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import '../styles/TrainingConfig.css';

/**
 * People panel — settings.
 *
 * Edit only. A People panel is CREATED in one click from the dashboard's Add menu, because it
 * has no filters to point at: it aggregates the snapshots the training panels have already
 * built. So the only things there are to set are its name and who can see it, and putting a
 * wizard in front of that would invent a configuration step the panel does not have.
 *
 * Reuses TrainingConfig.css rather than adding a stylesheet — the classes are global, and a
 * second sheet defining `tm-card` differently would restyle the training wizard (Note #15).
 */
function PeopleConfig() {
  const navigate = useNavigate();
  const { setupId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('People');
  const [isPublic, setIsPublic] = useState(true);
  const [ownerOnlyEdit, setOwnerOnlyEdit] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/people/${setupId}/config`, { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Could not read this panel (${r.status})`);
      setName(body.name || 'People');
      setIsPublic(!!body.is_public);
      setOwnerOnlyEdit(!!body.owner_only_edit);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setupId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`/api/people/${setupId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          name: name.trim(), is_public: isPublic, owner_only_edit: ownerOnlyEdit,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Save failed (${r.status})`);
      navigate(`/training/people/${setupId}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="tm-config"><div className="tm-loading">Loading…</div></div>;
  }

  return (
    <div className="tm-config">
      <div className="tm-head">
        <button className="tm-back" onClick={() => navigate('/training')}>← Back</button>
        <div>
          <h1>People panel settings</h1>
          <p className="tm-sub">
            This panel has no filters. It reads the snapshots your Training Traceability
            panels have already built, so there is nothing here but its name and who sees it.
          </p>
        </div>
      </div>

      {error && <div className="tm-error">{error}</div>}

      <div className="tm-card">
        <label className="tm-label" htmlFor="pp-name">Panel name</label>
        <input
          id="pp-name"
          className="tm-text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. People"
        />
      </div>

      <div className="tm-card">
        <label className="tm-check">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
          <span>Share with everyone in this account</span>
        </label>
        <label className="tm-check">
          <input
            type="checkbox"
            checked={ownerOnlyEdit}
            onChange={(e) => setOwnerOnlyEdit(e.target.checked)}
          />
          <span>Only I can edit this panel</span>
        </label>
      </div>

      <div className="tm-actions">
        <button className="tm-btn-secondary" onClick={() => navigate('/training')}>Cancel</button>
        <button className="tm-btn-primary" disabled={!name.trim() || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

export default PeopleConfig;
